import React, { useState, useEffect } from 'react';
import { Card, Alert, Spin, Typography, Space, Empty } from 'antd';
import { EyeOutlined, ClockCircleOutlined, CalendarOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const { Title, Text } = Typography;

const StaffPreviewTimeline = ({ patientId }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timelineData, setTimelineData] = useState(null);
  const [staffPreviewInfo, setStaffPreviewInfo] = useState(null);

  useEffect(() => {
    const fetchPreviewData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch staff preview data using clinical authentication
        const response = await axios.get(`/portal/admin/preview/${patientId}/timeline`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const data = response.data;
        
        // Extract staff preview metadata
        if (data._staffPreview) {
          setStaffPreviewInfo(data._staffPreview);
          // Remove metadata from the data
          const { _staffPreview, ...portalData } = data;
          setTimelineData(portalData);
        } else {
          setTimelineData(data);
        }

      } catch (err) {
        console.error('Error fetching staff preview timeline:', err);
        setError(err.response?.data?.message || 'Failed to load timeline preview');
      } finally {
        setLoading(false);
      }
    };

    if (patientId && token) {
      fetchPreviewData();
    }
  }, [patientId, token]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Preview Error"
        description={error}
        type="error"
        showIcon
        style={{ margin: '20px' }}
      />
    );
  }

  return (
    <div>
      {/* Staff Preview Info Card */}
      {staffPreviewInfo && (
        <Card 
          size="small" 
          style={{ 
            marginBottom: '20px',
            background: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)',
            border: '1px solid #91d5ff'
          }}
        >
          <Space>
            <EyeOutlined style={{ color: '#1890ff' }} />
            <Text strong style={{ color: '#1890ff' }}>Staff Preview Mode - Timeline</Text>
            <Text type="secondary">|</Text>
            <Text type="secondary">
              <ClockCircleOutlined style={{ marginRight: '4px' }} />
              {new Date(staffPreviewInfo.previewedAt).toLocaleString()}
            </Text>
            <Text type="secondary">|</Text>
            <Text type="secondary">Previewed by: {staffPreviewInfo.previewedBy}</Text>
          </Space>
        </Card>
      )}

      {/* Timeline Content */}
      <div style={{ 
        border: '2px dashed #d9d9d9',
        borderRadius: '8px',
        padding: '20px',
        background: '#fafafa',
        minHeight: '400px'
      }}>
        <div style={{ 
          background: '#fff',
          borderRadius: '6px',
          padding: '20px'
        }}>
          <Title level={3} style={{ marginBottom: '20px' }}>
            <CalendarOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            Patient Timeline
          </Title>
          
          {timelineData && timelineData.data && timelineData.data.length > 0 ? (
            <div>
              {timelineData.data.map((item, index) => (
                <Card 
                  key={index}
                  size="small"
                  style={{ marginBottom: '12px' }}
                  title={
                    <Space>
                      <Text strong>{item.type || 'Event'}</Text>
                      <Text type="secondary">
                        {item.date ? new Date(item.date).toLocaleDateString() : 'No date'}
                      </Text>
                    </Space>
                  }
                >
                  <Text>{item.description || item.title || 'No description available'}</Text>
                  {item.details && (
                    <div style={{ marginTop: '8px' }}>
                      <Text type="secondary">{item.details}</Text>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          ) : (
            <Empty 
              description="No timeline events available for this patient"
              style={{ padding: '40px' }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffPreviewTimeline;
